/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Fri Dec 22 2023
 **/

import { useEffect, useState } from "react";

import { _T } from "@rfice/language";
import { useProperties } from "@/frameworks/libraries/properties";
import { LocalData } from "@/frameworks/libraries/storage";
import { Icon, TabContainer, TabContent, TabItem, Tooltip, Typography } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { useEmoji } from "@/frameworks/core/API";

import * as S from "../private/emotion";
import { POPOVER_ID } from "../../../commons";
import { usePopover } from "../../../hooks";
import SystemEmoticon from "../EmoticonPickers/SystemEmoticon";

const staticIcon = [
	{
		name: "current",
		emoticonPaths: [],
		representativeImagePath: ""
	},
	{
		name: "emoji",
		emoticonPaths: [],
		representativeImagePath: ""
	}
];
const itemsPerPage = 6;

export default function EmoticonPicker({
	onSelectEmoticon
}: {
	onSelectEmoticon: (emoticon: { name: string; path?: string }) => void;
}) {
	const properties = useProperties();
	const popover = usePopover();
	const [emoticonList, setEmoticonList] = useState<WebServices.Emoji.List.Data>(staticIcon);
	const [pageState, setPageState] = useState(0);
	const [emoticonPaginateArray, setEmoticonPaginateArray] = useState<WebServices.Emoji.List.Data[]>([staticIcon]);

	const emojiAPI = useEmoji({
		onSuccess() {
			const { data } = emojiAPI.list.result;
			if (data) {
				setEmoticonList((prev) => [...prev, ...data]);
			}
		}
	});

	const emoticonPaginate = () => {
		const paginatedArray: WebServices.Emoji.List.Data[] = [];
		for (let i = 0; i < emoticonList.length; i += itemsPerPage) {
			const page = emoticonList.slice(i, i + itemsPerPage);
			paginatedArray.push(page);
		}
		return paginatedArray;
	};

	useEffect(() => {
		emojiAPI.list.execute();
	}, []);

	useEffect(() => {
		if (!emoticonList) return;
		setEmoticonPaginateArray(emoticonPaginate());
	}, [emoticonList]);

	const handleNext = () => {
		setPageState(pageState + 1);
	};

	const handlePrev = () => {
		setPageState(pageState - 1);
	};

	useEffect(() => {
		const emot = LocalData.get("cel");
		if (emot) {
			const newData = emoticonList.map((item) => {
				if (item.name === "current") {
					return {
						name: "current",
						representativeImagePath: "",
						emoticonPaths: emot ? emot : []
					};
				} else {
					return item;
				}
			});

			setEmoticonList(newData);
		}
	}, []);

	//@Description
	//otherEmotions 는 이모티콘 리스트 앞에 존재한는 두개 (최근 사용 및 감정표현) 을 제외하기 위한 변수
	const otherEmoticons = emoticonList.slice(2);
	//어드민에서 이모티콘을 삭제하면 삭제되었음에도 불구하고 최신 사용리스트에 데이터가 남아있어 이미지가 뜨지않는 오류 발생 이를 해결하기 위한 변수명
	const currentEmoticonPath =
		emoticonList[0] &&
		otherEmoticons.flatMap(({ emoticonPaths }) =>
			emoticonPaths.filter((emoticon) => emoticonList?.[0].emoticonPaths.includes(emoticon))
		);

	return (
		<S.Wrapper>
			<div style={{ width: "100%", height: "100%" }}>
				<TabContainer activeId={emoticonPaginateArray[pageState][0].name} allActive={true}>
					<S.RepresentativeWrapper>
						{pageState > 0 && <Icon name="Left24" size={20} onClick={handlePrev} />}
						<S.WrapperTab>
							{emoticonPaginateArray[pageState]?.map((emoticonObj, index) =>
								index === 0 && emoticonObj.name === "current" ? (
									<TabItem id={"current"} key={emoticonObj.name}>
										<Icon name="Star24" />
									</TabItem>
								) : index === 1 && emoticonObj.name === "emoji" ? (
									<TabItem id={"emoji"} key={emoticonObj.name}>
										<Icon name="Emoji24" />
									</TabItem>
								) : (
									<Tooltip
										hasIcon={false}
										message={emoticonObj.name}
										key={emoticonObj.representativeImagePath}
									>
										<TabItem id={emoticonObj.name}>
											<S.RepresentativeEmoji
												src={`${properties.default.webDomain}${emoticonObj.representativeImagePath}`}
												alt="represnetive emoticon"
											/>
										</TabItem>
									</Tooltip>
								)
							)}
						</S.WrapperTab>
						{pageState < emoticonPaginateArray.length - 1 && (
							<Icon name="Right24" size={20} onClick={handleNext} />
						)}
					</S.RepresentativeWrapper>
					{/**
					 * 최근 사용한 이모티콘
					 */}
					<TabContent activeId={"current"} key="current">
						<S.WrapContentEmotion>
							<Maybe test={Boolean(currentEmoticonPath.length > 0)}>
								<Typography typoColor="neutral_80_100" typoSize="Title2">
									{_T("최근 사용한 이모지")}
								</Typography>
							</Maybe>
							<S.DisplayEmotion>
								<S.EmotionList>
									{currentEmoticonPath.map((emoticonPath) => (
										<S.EmojiItem
											key={emoticonPath}
											onClick={() => {
												onSelectEmoticon?.({ name: "current", path: emoticonPath });
											}}
											src={`${properties.default.webDomain}${emoticonPath}`}
											alt="emoticon item"
										/>
									))}
								</S.EmotionList>
							</S.DisplayEmotion>
						</S.WrapContentEmotion>
						<S.Info>
							<Typography typoSize="Body5" typoColor="warning_60_100">
								{_T("캐시를 삭제 하면 최근 사용한 이모티콘이 삭제 됩니다.")}
							</Typography>
						</S.Info>
					</TabContent>

					{/**
					 * 시스템 이모지
					 */}
					<SystemEmoticon
						onSelectEmoticon={(emoticon) => {
							onSelectEmoticon(emoticon);
							popover.hide(POPOVER_ID.CHAT_EMOTICON_PICKER);
						}}
					/>

					{emoticonList.map(
						(e, index) =>
							index > staticIcon.length - 1 && (
								<TabContent activeId={e.name} key={e.name}>
									<S.DisplayEmotion style={{ padding: "16px 0" }}>
										<Typography typoColor="neutral_80_100" typoSize="Title2">
											{e.name}
										</Typography>
										<S.EmotionList>
											{e.emoticonPaths.map((emoticonPath) => (
												<S.EmojiItem
													key={emoticonPath}
													onClick={() => {
														onSelectEmoticon?.({ name: e.name, path: emoticonPath });
													}}
													src={`${properties.default.webDomain}${emoticonPath}`}
													alt="emoticon item"
												/>
											))}
										</S.EmotionList>
									</S.DisplayEmotion>
								</TabContent>
							)
					)}
				</TabContainer>
			</div>
		</S.Wrapper>
	);
}
